
"use client";

import { useRef } from "react";
import { useParams, useRouter } from "next/navigation";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import Link from "next/link";
import { syallabus } from "../page";
import { motion, useInView } from "framer-motion";
import { BookOpen, Clock, Users, Star } from "lucide-react";

interface Week {
  week: string;
  title: string;
  topics: string[];
  tools?: string[];
}

interface Course {
  id: string;
  title: string;
  instructor: string;
  instructorTitle: string;
  instructorImage?: string;
  description: string;
  color: string;
  isProgramming: boolean;
  weeks: number;
  baseStudents: number;
  syllabus: Week[];
}


export default function SyllabusDetail() {
  const params = useParams();
  const router = useRouter();
  const id = params?.id as string;

  const course = (syallabus as Course[]).find((c) => c.id === id);

  if (!course) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-gray-50">
        <h1 className="text-3xl font-bold text-gray-800">Course Not Found</h1>
        <Link href="/syallabus" className="px-6 py-3 bg-blue-600 text-white rounded-lg">
          Back to Courses
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-gray-100">
      <Header />

      {/* ---------- HERO SECTION ---------- */}
      <section
        className={`relative h-[24rem] bg-gradient-to-r ${course.color} text-white flex items-center justify-center`}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col items-center text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold mb-3">{course.title}</h1>
        </div>
      </section>

      {/* ---------- COURSE OVERVIEW ---------- */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            Course Overview
          </h2>

          <p className="text-gray-700 mb-8 leading-relaxed">{course.description}</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Duration: {course.isProgramming ? `${course.weeks} weeks` : "6 months"}
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Rating: 4.8 / 5
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              {course.baseStudents.toLocaleString()}+ Students
            </div>
          </div>
        </motion.div>
      </section>

      {/* ---------- SYLLABUS TIMELINE WITH GOOGLE MAP BACKGROUND ---------- */}
      <section className="relative w-full py-20 overflow-hidden">
        {/* Google Map background */}
        <div className="absolute inset-0">
          <iframe
            title="Google Map Background"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.9023480852357!2d90.39945247434647!3d23.750868689138957!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b8bf7f6a6ff7%3A0x7b2b8bbdcfe76a4b!2sGoogle%20Bangladesh!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
            width="100%"
            height="100%"
            allowFullScreen
            loading="lazy"
            className="w-full h-full opacity-40 grayscale contrast-125"
          ></iframe>
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[3px]"></div>
        </div>

        {/* Timeline content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Detailed Syllabus
          </h2>

          {/*    <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-[3px] bg-gradient-to-b from-blue-400 to-blue-100 h-full hidden md:block" />

            {course.syllabus.map((week, i) => {
              const ref = useRef(null);
              const isInView = useInView(ref, { amount: 0.4, once: false });
              const isEven = i % 2 === 0;
              const sideClass = isEven
                ? "md:flex-row-reverse md:text-right"
                : "md:flex-row md:text-left";

              return (
                <motion.div
                  key={i}
                  ref={ref}
                  initial={{ opacity: 0, y: 80, scale: 0.95 }}
                  animate={
                    isInView
                      ? { opacity: 1, y: 0, scale: 1 }
                      : { opacity: 0, y: 80, scale: 0.95 }
                  }
                  transition={{
                    duration: 0.6,
                    ease: "easeOut",
                    delay: i * 0.1,
                  }}
                  className={`relative flex flex-col md:items-center justify-between mb-12 md:gap-8 ${sideClass}`}
                >
                  <div
                    className={`w-full md:w-5/12 bg-white rounded-xl shadow-lg p-6 border-l-4 ${
                      isEven ? "md:border-blue-600" : "md:border-cyan-600"
                    }`}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Week {week.week}: {week.title}
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 mb-3">
                      {week.topics.map((t, idx) => (
                        <li key={idx}>{t}</li>
                      ))}
                    </ul>
                    {week.tools && (
                      <p className="text-sm text-gray-500">
                        <span className="font-medium text-gray-800">Tools:</span>{" "}
                        {week.tools.join(", ")}
                      </p>
                    )}
                  </div>

                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-md hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        */}
          <div className="relative">
            {/* Center vertical timeline line */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[3px] bg-gradient-to-b from-blue-400 to-blue-100 h-full hidden md:block" />

            <div className="flex flex-col space-y-20">
              {course.syllabus.map((week, i) => {
                const ref = useRef(null);
                const isInView = useInView(ref, { amount: 0.4, once: false });
                const isEven = i % 2 === 0;

                return (
                  <motion.div
                    key={i}
                    ref={ref}
                    initial={{ opacity: 0, y: 60 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className={`relative flex flex-col md:flex-row md:items-center ${isEven ? "md:flex-row-reverse" : ""
                      }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-md hidden md:block z-10" />

                    {/* Card */}
                    <div
                      className={`w-full md:w-5/12 bg-white rounded-2xl shadow-lg p-8 border-l-4 ${isEven ? "md:border-cyan-600" : "md:border-blue-600"
                        } ${isEven ? "md:ml-auto" : "md:mr-auto"}`}
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {week.week}: {week.title}
                      </h3>
                      <ul className="list-disc list-inside text-gray-700 space-y-1 mb-3">
                        {week.topics.map((t, idx) => (
                          <li key={idx}>{t}</li>
                        ))}
                      </ul>
                      {week.tools && (
                        <p className="text-sm text-gray-500">
                          <span className="font-medium text-gray-800">Tools:</span>{" "}
                          {week.tools.join(", ")}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

        </div>
      </section>

      {/* ---------- CTA SECTION ---------- */}
      <section className="max-w-5xl mx-auto px-6 pb-16 text-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => router.push("/contact")}
          className={`px-10 py-4 rounded-xl font-bold text-white bg-gradient-to-r ${course.color} shadow-lg`}
        >
          Enroll Now
        </motion.button>

        <div className="mt-6">
          <Link href="/syallabus" className="text-blue-600 hover:underline text-sm">
            ← Back to all courses
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
